import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, UserCheck, Target, AlertTriangle } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/utils';
import { KPICard } from './OverviewTab';

const segmentColors = {
  platinum: 'bg-slate-700',
  gold: 'bg-amber-500',
  silver: 'bg-slate-400',
  bronze: 'bg-orange-700'
};

export const ClientsTab = ({ loading, clientData }) => {
  if (loading) return <div className="text-center py-12">Loading client analytics...</div>;
  if (!clientData) return null;

  return (
    <div className="space-y-6">
      {/* Client KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard title="Total Clients" value={clientData.summary.total_clients} subtitle={`${clientData.summary.active_clients} active`} icon={Users} color="blue" />
        <KPICard title="New This Month" value={clientData.summary.new_this_month} subtitle="vs last month" icon={UserCheck} trend={clientData.acquisition.growth > 0 ? 'up' : 'down'} trendValue={`${clientData.acquisition.growth} clients`} color="emerald" />
        <KPICard title="Retention Rate" value={`${clientData.retention.retention_rate}%`} subtitle={`${clientData.summary.churned_count} churned`} icon={Target} color="teal" />
        <KPICard title="At Risk" value={clientData.summary.at_risk_count} subtitle="Need attention" icon={AlertTriangle} color="amber" />
      </div>

      {/* Client Health & Segmentation */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Client Segmentation</CardTitle>
            <CardDescription>Based on lifetime value</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(clientData.segmentation).map(([segment, data]) => (
                <div key={segment} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full ${segmentColors[segment]}`} />
                      <span className="font-medium capitalize">{segment}</span>
                      <span className="text-xs text-muted-foreground">{data.threshold}</span>
                    </div>
                    <Badge variant="secondary">{data.count}</Badge>
                  </div>
                  <Progress value={(data.count / clientData.summary.total_clients) * 100} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lifetime Value Stats</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="text-center p-6 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl">
                <p className="text-sm text-muted-foreground">Average LTV</p>
                <p className="text-4xl font-bold text-emerald-600">{formatCurrency(clientData.lifetime_value.average_ltv)}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Highest LTV</p>
                  <p className="text-xl font-bold">{formatCurrency(clientData.lifetime_value.max_ltv)}</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Total LTV</p>
                  <p className="text-xl font-bold">{formatCurrency(clientData.lifetime_value.total_ltv)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* At-Risk Clients Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            At-Risk Clients
          </CardTitle>
          <CardDescription>Clients showing signs of churn - need attention</CardDescription>
        </CardHeader>
        <CardContent>
          {clientData.at_risk_clients?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Health Score</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead>Orders</TableHead>
                  <TableHead>Last Order</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clientData.at_risk_clients.map((client) => (
                  <TableRow key={client.client_id}>
                    <TableCell className="font-medium">{client.client_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={client.health_score} className="w-16 h-2" />
                        <span className="text-sm">{client.health_score}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">{formatCurrency(client.revenue)}</TableCell>
                    <TableCell>{client.orders}</TableCell>
                    <TableCell>{client.last_order ? formatDate(client.last_order) : '-'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
                        {client.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <UserCheck className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>No at-risk clients identified</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
